import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, Phone } from "lucide-react";
import { CmykDots } from "@/components/Reveal";
import { scrollToId } from "@/App";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { delay: 0.2 + i * 0.13, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

const STATS = [
  ["FAST TURN-AROUND", "on most print jobs"],
  ["PAN-MUMBAI", "doorstep delivery"],
  ["70–350 GSM", "clean white paper stock"],
];

export default function Hero() {
  const imgWrap = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgWrap, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section className="relative min-h-screen flex items-center pt-28 pb-16 lg:pt-32" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* left: kinetic type */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-3 mb-8"
          >
            <CmykDots />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-mist">
              Document printing &amp; binding — Mumbai
            </span>
          </motion.div>

          <h1 className="font-display font-semibold leading-[0.95] tracking-tight text-[clamp(3rem,7.4vw,6.1rem)]">
            {["EVERY PAGE,", "PRINTED TO", "PERFECTION."].map((text, i) => (
              <span key={text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "text-cyanbrand" : i === 2 ? "text-outline" : "text-paper"}`}
                  variants={line}
                  custom={i}
                  initial="hidden"
                  animate="show"
                >
                  {text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.6 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-mist leading-relaxed"
          >
            PRO PRINT SOLUTION LLP is Mumbai&apos;s express document press. Reports, theses,
            decks and legal files — printed, bound and delivered fast across Mumbai.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="mt-10 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4"
          >
            <button
              data-testid="hero-quote-cta"
              onClick={() => scrollToId("contact")}
              className="group inline-flex items-center justify-center sm:justify-start gap-2 w-full sm:w-auto bg-cyanbrand text-ink font-bold text-base sm:text-sm px-7 py-4 rounded-sm hover:bg-paper transition-colors duration-300"
            >
              Get a Quote
              <ArrowDownRight size={17} className="transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <a
              data-testid="hero-call-cta"
              href="tel:+917021504681"
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto border border-edge text-paper font-mono text-sm sm:text-xs tracking-[0.15em] px-6 py-4 rounded-sm hover:border-cyanbrand hover:text-cyanbrand transition-colors duration-300"
            >
              <Phone size={15} /> +91 70215 04681
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-edge pt-8"
          >
            {STATS.map(([big, small]) => (
              <div key={big}>
                <div className="font-display font-semibold text-xl text-paper">{big}</div>
                <div className="font-mono text-[11px] text-mist mt-1.5 leading-relaxed">{small}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* right: treated press photo */}
        <div className="lg:col-span-5" ref={imgWrap}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden border border-edge rounded-sm aspect-[4/5]">
              <motion.img
                src="https://images.unsplash.com/photo-1503694978374-8a2fa686963a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="High-speed digital printing press producing documents"
                style={{ y }}
                className="absolute inset-0 w-full h-[112%] object-cover"
                data-testid="hero-press-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/80">
                  Digital press floor — Mumbai
                </span>
                <CmykDots />
              </div>
            </div>
            {/* registration corner marks */}
            <span className="absolute -top-3 -left-3 w-6 h-6 border-t-2 border-l-2 border-mag" aria-hidden="true" />
            <span className="absolute -bottom-3 -right-3 w-6 h-6 border-b-2 border-r-2 border-cyanbrand" aria-hidden="true" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
